import React, { useRef, useCallback, useEffect } from "react";
import type { FilterState } from "../../ProjectList/types";

interface UseFilterRefsProps {
  filters: FilterState;
}

export const useFilterRefs = ({ filters }: UseFilterRefsProps) => {
  const chipRefs = useRef<Map<string, React.RefObject<HTMLDivElement>>>(
    new Map(),
  );

  const getOrCreateChipRef = useCallback((key: string) => {
    let ref = chipRefs.current.get(key);
    if (!ref) {
      ref = React.createRef<HTMLDivElement>();
      chipRefs.current.set(key, ref);
    }
    return ref;
  }, []);

  useEffect(() => {
    const currentKeys = new Set<string>();

    if (filters.search) currentKeys.add("search");

    if (filters.userIds && filters.userIds.length > 0) {
      if (filters.userIds.length === 1) {
        currentKeys.add(`assignee-${filters.userIds[0]}`);
      } else {
        currentKeys.add("assignee-consolidated");
      }
    }

    if (filters.statuses && filters.statuses.length > 0) {
      if (filters.statuses.length === 1) {
        currentKeys.add(`status-${filters.statuses[0]}`);
      } else {
        currentKeys.add("status-consolidated");
      }
    }

    if (filters.priorities && filters.priorities.length > 0) {
      if (filters.priorities.length === 1) {
        currentKeys.add(`priority-${filters.priorities[0]}`);
      } else {
        currentKeys.add("priority-consolidated");
      }
    }

    if (filters.estimates && filters.estimates.length > 0) {
      if (filters.estimates.length === 1) {
        currentKeys.add(`estimate-${filters.estimates[0]}`);
      } else {
        currentKeys.add("estimate-consolidated");
      }
    }

    if (filters.labels && filters.labels.length > 0) {
      if (filters.labels.length === 1) {
        currentKeys.add(`label-${filters.labels[0]}`);
      } else {
        currentKeys.add("label-consolidated");
      }
    }

    if (filters.dueDates) {
      const activeDueDates = Object.entries(filters.dueDates).filter(
        ([_, value]) => value,
      );
      if (activeDueDates.length > 0) {
        if (activeDueDates.length === 1) {
          currentKeys.add(`dueDate-${activeDueDates[0][0]}`);
        } else {
          currentKeys.add("dueDate-consolidated");
        }
      }
    }

    const keysToDelete: string[] = [];
    chipRefs.current.forEach((_, key) => {
      if (!currentKeys.has(key)) {
        keysToDelete.push(key);
      }
    });

    if (keysToDelete.length > 0) {
      keysToDelete.forEach((key) => chipRefs.current.delete(key));
    }
  }, [filters]);

  return {
    getOrCreateChipRef,
  };
};
